import { Breakpoint, Theme, ThemeOptions, createTheme } from "@mui/material";
import { useMemo } from "react";
import {
  TSpacingConfig,
  parsePalette,
  parseSpacing,
  parseBreakpoints,
  parseTypography,
} from "../parsers";
import { TRemoteThemeResponse } from "../types";

export type TTheme = {
  spacing?: TSpacingConfig;
  breakpoints?: Partial<{ [key in Breakpoint]: number }>;
  palette?: ThemeOptions["palette"];
  typography?: ThemeOptions["typography"];
} & Omit<ThemeOptions, "spacing" | "breakpoint">;

export type TUseTheme = (local: TTheme, remote?: TRemoteThemeResponse) => Theme;

export const useTheme: TUseTheme = (local, remote) => {
  const {
    spacing: localSpacing,
    palette: localPalette,
    breakpoints: localBreakpoints,
    typography: localTypography,
    ...rest
  } = local;
  const theme = new Map();
  // generate breakpoints
  const breakpoints = useMemo(() => {
    return parseBreakpoints(localBreakpoints, remote?.theme?.breakpoints);
  }, [localBreakpoints, remote?.theme?.breakpoints]);
  // generate palette
  const palette = useMemo(() => {
    return parsePalette(localPalette, remote?.theme?.palette);
  }, [localPalette, remote?.theme?.palette]);
  // generate typography
  const typography = useMemo(() => {
    return parseTypography(localTypography, remote?.theme?.typography);
  }, [localTypography, remote?.theme?.typography]);
  // generate spacing
  const spacing = useMemo(() => {
    return parseSpacing(localSpacing, remote?.theme?.spacing);
  }, [localSpacing, remote?.theme?.spacing]);
  // set theme properties
  if (breakpoints) theme.set("breakpoints", { values: breakpoints });
  if (palette) theme.set("palette", palette);
  if (spacing) theme.set("spacing", spacing);
  if (remote?.contents) theme.set("contents", remote?.contents);
  if (typography) theme.set("typography", typography);
  return createTheme({ ...rest, ...Object.fromEntries(theme) });
};
